import { ConfirmDialogConfig } from '@agent-ds/shared/components/confirm-dialog/confirm-dialog-config';
import { ConfirmDialogComponent } from '@agent-ds/shared/components/confirm-dialog/confirm-dialog.component';
import { CompanyDetail, Job, JobAzCheckWageErrorDetail } from '@agent-ds/shared/interfaces';
import {
  AzCheckRow,
  ENTERPRISE_PREFIX,
  JobAzCheckConsultingInfo,
  JobAzCheckConsultingItem,
  JobAzCheckTiming,
  SALARY_TABLE_FIELD_KEY_PREFIX
} from '@agent-ds/shared/interfaces/job-az-check';
import { FieldMeta, FormMeta, GroupMeta, RowMeta } from '@agent-ds/shared/models';
import { DialogService, JobApiService } from '@agent-ds/shared/services';
import { JobAzCheckService } from '../../services/job-az-check.service';
import { getWageCheckFieldLabel, resolveWageCheckField } from '../../../../util/job-salary-table';
import { getGradCategoryLabel } from '@agent-ds/shared/util/salary-table-key';
import { formatNumber, getValueFromObject } from '@agent-ds/shared/util/util';
import { Component, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';

@Component({
  selector: 'ag-job-az-check-step-consultation',
  templateUrl: './job-az-check-step-consultation.component.html',
  styleUrls: ['./job-az-check-step-consultation.component.scss'],
})
export class JobAzCheckStepConsultationComponent implements OnDestroy {
  contactNote = '';
  consultationContents: { [fieldKey: string]: string } = {};
  copySuccessMessage = false;
  private copySuccessTimeout: any;
  private consultFormMetaCache: { [fieldKey: string]: FormMeta } = {};

  constructor(
    public jobAzCheckService: JobAzCheckService,
    private dialogService: DialogService,
    private jobApiService: JobApiService,
  ) {}

  ngOnDestroy(): void {
    if (this.copySuccessTimeout) {
      clearTimeout(this.copySuccessTimeout);
    }
  }

  /**
   * 項目名とは別で表示したいラベルを取得する
   * enterprise_address1等、項目名以外に表示したいタイトルなどがある場合に使用
   * 例）「本社所在地 郵便番号・都道府県」 の 「本社所在地」 部分
   */
  getConsultationRowLabelOverride(row: AzCheckRow): string {
    const consultationLabelOverrides: { [fieldKey: string]: string } = {
      [`${ENTERPRISE_PREFIX}zipAndPrefecture`]: '本社所在地',
      [`${ENTERPRISE_PREFIX}address1`]: '本社所在地',
      [`${ENTERPRISE_PREFIX}address2`]: '本社所在地',
      [`${ENTERPRISE_PREFIX}address3`]: '本社所在地',
    };
    return consultationLabelOverrides[row.fieldKey] || '';
  }

  // 企画相談画面では表示専用にするため、チェック内容確認画面の編集用formMetaとは別のdisabled版を作って使う
  // （row.formMetaをそのまま書き換えるとチェック内容確認画面側の入力フォームまで操作不可になってしまうため）
  getConsultFormMeta(row: AzCheckRow): FormMeta {
    if (!this.consultFormMetaCache[row.fieldKey]) {
      this.consultFormMetaCache[row.fieldKey] = {
        ...row.formMeta,
        disabled: true,
        groups: row.formMeta.groups.map((group) => this.toReadonlyGroup(group)),
      };
    }
    return this.consultFormMetaCache[row.fieldKey];
  }

  /**
   * 折りたたみ表示のグループ（セミナー・面接情報）を表示専用に変換する。
   * 折りたたみヘッダの「入力／削除」ボタンはmetadata.disabledを参照しないため、
   * そのままでは表示専用の企画相談画面から入力値を削除できてしまう。
   * 折りたたみを解いてボタン自体を描画させず、見出しはグループタイトルとして残す。
   */
  private toReadonlyGroup(group: GroupMeta): GroupMeta {
    if (!group.collapsable) {
      return group;
    }

    return { ...group, collapsable: false, title: group.collapsedTitle };
  }

  copyConsultationDetail(savedTiming: JobAzCheckTiming): void {
    const job = this.jobAzCheckService.job;
    this.saveConsultingInfo(savedTiming).subscribe(() => {
      this.jobApiService.refreshEvent.emit();
      const text = this.buildConsultationTemplate(job);
      this.copyToClipboard(text).then(
        () => {
          this.copySuccessMessage = true;
          if (this.copySuccessTimeout) {
            clearTimeout(this.copySuccessTimeout);
          }
          this.copySuccessTimeout = setTimeout(() => {
            this.copySuccessMessage = false;
          }, 3000);
        },
        () => {
          const dialogConfig: ConfirmDialogConfig = ConfirmDialogConfig.createErrorMessageDialog([
            'クリップボードへのコピーに失敗しました。',
          ]);
          this.dialogService.open(ConfirmDialogComponent, dialogConfig);
        },
      );
    });
  }

  private saveConsultingInfo(savedTiming: JobAzCheckTiming): Observable<any> {
    const consultingInfo: JobAzCheckConsultingInfo = {
      savedTiming: savedTiming,
      selectedJobCategory: this.jobAzCheckService.selectedCategory,
      middlePermissionFlg: null,
      contactMessage: this.contactNote,
      consultingItems: this.buildConsultingItems(),
      resultDescription: '',
    };

    return this.jobApiService.saveConsultingInfo(this.jobAzCheckService.job.id, consultingInfo);
  }

  // 相談対象項目（通常項目・賃金チェック項目）を consultingTargetKey（=row.fieldKey）をキーとした連想配列にまとめる
  private buildConsultingItems(): { [consultingTargetKey: string]: JobAzCheckConsultingItem } {
    const items: { [consultingTargetKey: string]: JobAzCheckConsultingItem } = {};

    this.getConsultationRows().forEach((row) => {
      items[row.fieldKey] = this.buildConsultingItemFromRow(row);
    });

    this.getWageConsultationDetails().forEach((detail) => {
      items[detail.field] = this.buildConsultingItemFromWageDetail(detail);
    });

    return items;
  }

  private buildConsultingItemFromRow(row: AzCheckRow): JobAzCheckConsultingItem {
    const error = this.jobAzCheckService.getError(row.fieldKey);
    const model = row.useCompanyModel ? this.jobAzCheckService.companyData : this.jobAzCheckService.job;
    const fieldItems = this.getConsultationFieldItems(row, model);
    const labelOverride = this.getConsultationRowLabelOverride(row);
    const enterpriseDynamiData = this.jobAzCheckService.companyData && this.jobAzCheckService.companyData.dynamicData;
    const jobDynamiData = this.jobAzCheckService.job.dynamicData;
    const rawValue = this.getRawValue(row, jobDynamiData, enterpriseDynamiData);
    const itemsLabel = fieldItems.map((item) => item.label).join('\n') || row.fieldKey;

    return {
      consultingTargetKey: row.fieldKey,
      fieldName: row.fieldDefinition.fieldName,
      label: labelOverride ? `${itemsLabel}（${labelOverride}）` : itemsLabel,
      value: rawValue.value,
      visibleItems: rawValue.visibleItems,
      unit: row.fieldDefinition.unit,
      companionText: this.getCompanionText(row, enterpriseDynamiData),
      consultingContent: this.consultationContents[row.fieldKey] || '',
      errorType: error ? error.type : 'warning',
      errorMessage: error ? error.message : '',
    };
  }

  /**
   * 資本金・売上高・従業員数等、非公開／グループ連結チェックボックス（row.companionFieldNames）を持つ項目について、
   * enterpriseDynamicDataに保存済みの選択済みチェックボックス値（例：['非公開']）を文字列化する
   */
  private getCompanionText(row: AzCheckRow, dynamicData?: Record<string, any>): string | undefined {
    if (!row.companionFieldNames || !row.companionFieldNames.length) {
      return undefined;
    }

    if (!dynamicData) {
      return undefined;
    }

    const labels = row.companionFieldNames
      .map((fieldName) => dynamicData[fieldName])
      .map((rawValue) => (Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : []))
      .reduce((acc, values) => acc.concat(values), [] as string[])
      .filter((text) => !!text);

    return labels.length ? labels.join('、') : undefined;
  }

  /**
   * AzCheckRow で表示している項目のDynamicDataの値を取得する。
   * salaryTable・address・workplaceは1つのfieldNameが複数行に分割表示されるため、行ごとにvisibleItemsで対象項目を指定する。
   */
  private getRawValue(row: AzCheckRow, jobDynamicData: Record<string, any>, enterpriseDynamicData: Record<string, any>): { value: any; visibleItems?: string[] } {
    if (row.fieldDefinition.fieldName === 'salaryTable') {
      return {
        value: jobDynamicData.salaryTable,
        visibleItems: [row.fieldKey.replace(SALARY_TABLE_FIELD_KEY_PREFIX, '')],
      };
    }

    if (row.fieldDefinition.fieldName === 'address') {
      return {
        value: enterpriseDynamicData ? enterpriseDynamicData.address : undefined,
        visibleItems: [row.fieldKey.replace(ENTERPRISE_PREFIX, '')],
      };
    }

    if (row.fieldDefinition.fieldName === 'workplace') {
      return {
        value: jobDynamicData.workplace,
        visibleItems: ['address1'],
      };
    }

    return {
      value: row.useCompanyModel
        ? !!enterpriseDynamicData ? enterpriseDynamicData[row.fieldDefinition.fieldName] : ''
        : jobDynamicData[row.fieldDefinition.fieldName],
    };
  }

  /**
   * 賃金チェック結果の表示用ラベル（項目名＋卒業種別）を取得する。
   * detail.fieldはAPI側でwage_接頭辞と卒業種別を付与した一意キーのため、 resolveWageCheckField で元の WageCheckField に変換してから項目名を引く。
   * @param detail 賃金チェックエラー詳細
   * @returns 賃金チェック結果の表示用ラベル
   */
  private getWageFieldLabel(detail: JobAzCheckWageErrorDetail): string {
    const label = getWageCheckFieldLabel(resolveWageCheckField(detail.field) || '');
    if (!detail.gradCategory) {
      return label;
    }

    const gradCategoryLabel = getGradCategoryLabel(detail.gradCategory) || '種別なし';
    return `${label}（${gradCategoryLabel}）`;
  }

  private buildConsultingItemFromWageDetail(detail: JobAzCheckWageErrorDetail): JobAzCheckConsultingItem {
    const key = detail.field;
    const label = this.getWageFieldLabel(detail);

    return {
      consultingTargetKey: key,
      fieldName: 'wageCheck',
      label: label,
      consultingContent: this.consultationContents[key] || '',
      errorType: detail.type,
      errorMessage: detail.message,
      requiredValueLabel: detail.requiredValueLabel,
      actualValueLabel: detail.actualValueLabel,
      requiredValue: detail.requiredValue,
      actualValue: detail.actualValue,
      unit: detail.unit,
    };
  }

  // 相談対象（consultationTargets）にチェックが入っている項目のAzCheckRowを一覧取得する
  getConsultationRows(): AzCheckRow[] {
    const rows: AzCheckRow[] = [];
    this.jobAzCheckService.checkSections.forEach((section) => {
      section.rows.forEach((row) => {
        if (this.jobAzCheckService.consultationTargets[row.fieldKey]) {
          rows.push(row);
        }
      });
    });
    return rows;
  }

  // 相談対象（consultationTargets）にチェックが入っている賃金チェック結果を一覧取得する
  // 賃金チェックは1フィールドが卒業種別ごとに複数件になるため、AzCheckRowではなくJobAzCheckWageErrorDetail単位で管理する
  getWageConsultationDetails(): JobAzCheckWageErrorDetail[] {
    if (!this.jobAzCheckService.checkResult) {
      return [];
    }
    return this.jobAzCheckService.checkResult.validationErrors.wageDetails.filter(
      (detail) => this.jobAzCheckService.consultationTargets[detail.field],
    );
  }

  // 相談対象の賃金チェック結果の表示用に、項目名（ラベル）と値をペアで取得する
  getWageConsultationFieldItems(detail: JobAzCheckWageErrorDetail): { label: string; value: string }[] {
    return [
      {
        label: '項目',
        value: this.getWageFieldLabel(detail),
      },
      {
        label: detail.requiredValueLabel,
        value: detail.requiredValue != null ? `${formatNumber(detail.requiredValue, '1.0-2')}${detail.unit}` : '-',
      },
      {
        label: detail.actualValueLabel,
        value: detail.actualValue != null ? `${formatNumber(detail.actualValue, '1.0-2')}${detail.unit}` : '計算エラー',
      },
    ];
  }

  // 相談対象項目の表示用に、項目名（ラベル）と現在の入力値をペアで取得する
  getConsultationFieldItems(row: AzCheckRow, model: CompanyDetail | Job | null): { label: string; value: string }[] {
    const items: { label: string; value: string }[] = [];

    row.formMeta.groups.forEach((group) => {
      group.rows.forEach((innerRow) => {
        // セミナー案内・面接案内1のように行タイトルを持たない入力欄は、折りたたみ見出しを項目名とする
        const label = innerRow.title || (group.collapsable ? group.collapsedTitle : '');
        if (innerRow.hidden || !label) {
          return;
        }

        items.push({
          label: label,
          value: row.fieldKey.startsWith(SALARY_TABLE_FIELD_KEY_PREFIX)
            ? this.getRowDisplayValueForSalaryTableRow(model, innerRow)
            : this.getRowDisplayValue(model, innerRow),
        });
      });
    });

    return items;
  }

  private getRowDisplayValueForSalaryTableRow(model: any, innerRow: RowMeta): string {
    if (!['月給', '固定残業代補足', '固定の深夜割増手当', '給与補足'].includes(innerRow.title || '')) {
      return this.getRowDisplayValue(model, innerRow);
    }

    // 月給, 固定残業代補足, 固定の深夜割増手当, 給与補足は改行など表示内容を操作する必要があるため別で設定する
    const lines: string[] = [];
    let currentLineValues: string[] = [];

    if (innerRow.title === '固定残業代補足') {
      // 固定残業代補足の場合、設定した固定残業代か補足かわかるように最初に「<固定残業代>」を追加する
      lines.push('<固定残業代>');
    }

    const flushCurrentLine = () => {
      if (currentLineValues.length) {
        lines.push(currentLineValues.join(' '));
        currentLineValues = [];
      }
    };

    innerRow.fields.forEach((field) => {
      if (!field || !field.name || field.hidden) {
        return;
      }

      // field.nameは`dynamicData.salaryTable.lineBreak_xxx`のような完全修飾キーのため、末尾セグメントで判定する
      const fieldNameSuffix = field.name.split('.').pop() || '';
      if (fieldNameSuffix.startsWith('lineBreak_')) {
        // lineBreak_で始まる場合改行する
        flushCurrentLine();
        return;
      }

      if (field.type === 'label') {
        // defaultの値をそのまま表示する
        if (field.default) {
          currentLineValues.push(String(field.default));
        }
        return;
      }

      if (field.type === 'number') {
        const rawValue = getValueFromObject(model, field.name);
        if (rawValue == null || rawValue === '') {
          return;
        }
        // labelAfter(円や時間)がある場合一緒に表示する
        currentLineValues.push(`${rawValue}${field.labelAfter || ''}`);
        return;
      }

      if (field.type === 'textarea') {
        if (innerRow.title === '固定残業代補足' && fieldNameSuffix === 'fixedOvertimeDescriptionNonEditable') {
          // 固定残業代補足の場合、設定した固定残業代か補足かわかるように補足の前に「<補足>」を追加する
          lines.push('<補足>');
        }
        flushCurrentLine();
        const value =
          fieldNameSuffix === 'salaryDescriptionNonEditableDisplay'
            ? this.getSalaryDescriptionNonEditableDisplayValue(model, field.name)
            : this.getFieldDisplayValue(model, field);
        if (value) {
          lines.push(value);
        }
        return;
      }

      const value = this.getFieldDisplayValue(model, field);
      if (value) {
        currentLineValues.push(value);
      }
    });

    flushCurrentLine();

    return lines.join('\n');
  }

  private getSalaryDescriptionNonEditableDisplayValue(model: any, fieldName: string): string {
    const baseKey = fieldName.replace(/\.salaryDescriptionNonEditableDisplay$/, '');
    const texts = [
      getValueFromObject(model, `${baseKey}.salaryDescriptionSalaryByGraduateCategory`),
      getValueFromObject(model, `${baseKey}.salaryDescriptionFixedLateNightOvertime`),
      getValueFromObject(model, `${baseKey}.salaryDescriptionVariableWorkingHoursSystem`),
    ];
    return texts.filter((text) => !!text).join('\n');
  }

  private getRowDisplayValue(model: any, innerRow: RowMeta): string {
    // 郵便番号を含む行は「〒000-0000」の形で1つの値としてまとめる
    const zipFields = innerRow.fields.filter((field) => field && field.type === 'zip' && field.name);

    const otherFields = innerRow.fields.filter(
      (field) => field && field.name && !field.hidden && field.type !== 'hr' && field.type !== 'zip',
    );
    const otherValues = otherFields.map((field) => this.getFieldDisplayValue(model, field)).filter((value) => !!value);

    if (zipFields.length === 0) {
      return otherValues.join(' ');
    }

    const zipText = this.getZipDisplayValue(model, zipFields[0]);

    return [zipText, ...otherValues].filter((value) => !!value).join(' ');
  }

  private getZipDisplayValue(model: any, zipField: FieldMeta): string {
    const baseKey = zipField.name.replace(/\.\d+$/, '');
    const rawValue = getValueFromObject(model, baseKey);
    if (!rawValue) {
      return '';
    }
    return `〒${rawValue.substring(0, 3)}-${rawValue.substring(3)}`;
  }

  private getFieldDisplayValue(model: any, field: FieldMeta): string {
    if (field.type === 'label') {
      // labelの場合defaultの値を表示する
      return field.default;
    }

    const rawValue = getValueFromObject(model, field.name);
    if (rawValue == null || rawValue === '') {
      return '';
    }

    if (typeof rawValue === 'boolean') {
      if (!rawValue) {
        return '';
      }
      return field.labelAfter || field.labelBefore || 'あり';
    }

    if (field.type === 'number') {
      if (rawValue == null || rawValue === '') {
        return '';
      }

      // labelAfter(円や時間)がある場合一緒に表示する
      return `${rawValue}${field.labelAfter || ''}`;
    }

    if (Array.isArray(rawValue)) {
      return rawValue.map((v) => this.resolveOptionLabel(field, v)).join('、');
    }

    return this.resolveOptionLabel(field, rawValue);
  }

  // options が {code, name} 形式のマスタ選択肢の場合、コード値を表示名に変換する
  private resolveOptionLabel(field: FieldMeta, value: any): string {
    if (Array.isArray(field.options) && field.options.length && typeof field.options[0] === 'object') {
      const matched = field.options.find((option) => option && option.code === value);
      if (matched) {
        return matched.name;
      }
    }
    return String(value);
  }

  private buildConsultationTemplate(job: Job): string {
    const lines: string[] = [];
    lines.push(`■求人ID：${job.frontId}`);
    lines.push('');

    lines.push('■連絡事項');
    lines.push(this.contactNote || '（なし）');
    lines.push('');
    lines.push('■相談したい項目');

    const rows = this.getConsultationRows();
    const wageDetails = this.getWageConsultationDetails();
    if (!rows.length && !wageDetails.length) {
      lines.push('（なし）');
    } else {
      rows.forEach((row) => {
        const model = row.useCompanyModel ? this.jobAzCheckService.companyData : this.jobAzCheckService.job;
        lines.push('----------------------------------------');
        const labelOverride = this.getConsultationRowLabelOverride(row);
        if (labelOverride) {
          lines.push(labelOverride);
        }
        this.getConsultationFieldItems(row, model).forEach((item) => {
          lines.push(`${item.label}：${item.value || '（未入力）'}`);
        });

        const error = this.jobAzCheckService.getError(row.fieldKey);
        if (error) {
          lines.push(`アラート：${error.message}`);
        }

        lines.push(`相談したい内容：${this.consultationContents[row.fieldKey] || '（未入力）'}`);
      });

      wageDetails.forEach((detail) => {
        const key = detail.field;
        lines.push('----------------------------------------');
        this.getWageConsultationFieldItems(detail).forEach((item) => {
          lines.push(`${item.label}：${item.value || '（未入力）'}`);
        });
        lines.push(`アラート：${detail.message}`);
        lines.push(`相談したい内容：${this.consultationContents[key] || '（未入力）'}`);
        if (detail.calculationSteps.length > 0) {
          lines.push('計算式：');
          for (const calculationStep of detail.calculationSteps) {
            lines.push(`　${calculationStep.label} ${calculationStep.operands} = ${calculationStep.result} ${calculationStep.unit} ${calculationStep.description}`);
          }
        }
      });
    }

    return lines.join('\n');
  }

  private copyToClipboard(text: string): Promise<void> {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }

    return new Promise<void>((resolve, reject) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      try {
        if (document.execCommand('copy')) {
          resolve();
        } else {
          reject(new Error('execCommand copy failed'));
        }
      } catch (e) {
        reject(e);
      } finally {
        document.body.removeChild(textarea);
      }
    });
  }
}
